import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Ensure the AI base URL is absolute. A bare host (e.g. a Railway RAILWAY_PRIVATE_DOMAIN
 * reference: "aallie-ai-backend.railway.internal") is resolved by the browser relative to the
 * page → https://www.aallie.com/aallie-ai-backend.railway.internal/... — so add the scheme:
 * http:// for private/local hosts (*.railway.internal, localhost, 127.x), https:// otherwise.
 * Absolute URLs and paths starting with "/" are kept; trailing slashes are removed.
 */
export const normalizeBaseUrl = (value) => {
    let url = String(value || '').trim();
    while (url.endsWith('/') && url.length > 1) url = url.slice(0, -1);
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url) || url.startsWith('/')) return url;
    const isPrivateHost = /^(localhost|127\.\d+\.\d+\.\d+|[^/:]+\.railway\.internal)(:\d+)?(\/|$)/i.test(url);
    return `${isPrivateHost ? 'http' : 'https'}://${url}`;
};

/** The AI service serves every endpoint under /v1 (its global prefix); add it unless already there. */
export const AI_API_VERSION = '/v1';
export const withAiApiVersion = (baseUrl) => {
    if (!baseUrl) return baseUrl;
    return baseUrl.endsWith(AI_API_VERSION) ? baseUrl : `${baseUrl}${AI_API_VERSION}`;
};

export const AI_API_BASE_URL = withAiApiVersion(normalizeBaseUrl(process.env.REACT_APP_AI_API_URL));

/**
 * Shared fetchBaseQuery factory for every RTK Query API.
 *
 * @param {Object}  options
 * @param {string}  options.baseUrl      - Base URL of the API.
 * @param {boolean} [options.auth=true]  - Attach `Authorization: Bearer <Token>` from the auth slice.
 * @param {string}  [options.credentials] - fetch credentials mode ('include' | 'same-origin' | 'omit').
 * @param {Object}  [options.headers]    - Static headers added to every request.
 * @param {number}  [options.timeout]    - Request timeout in ms.
 */
export const createBaseQuery = ({ baseUrl, auth = true, credentials, headers = {}, timeout } = {}) =>
    fetchBaseQuery({
        baseUrl,
        credentials,
        timeout,
        prepareHeaders: (requestHeaders, { getState }) => {
            Object.entries(headers).forEach(([key, value]) => requestHeaders.set(key, value));

            if (auth) {
                const Token = getState().auth.Token;
                if (Token) {
                    requestHeaders.set('Authorization', `Bearer ${Token}`);
                }
            }

            return requestHeaders;
        },
    });

// ─── AI service errors ───────────────────────────────────────────────────────

// Error codes returned by aallie-ai-service (see its README "Error contract"),
// plus client-side codes for failures that never reached the service.
export const AI_ERROR_CODES = {
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    ORIGIN_NOT_ALLOWED: 'ORIGIN_NOT_ALLOWED',
    STEP_DEPENDENCY_MISSING: 'STEP_DEPENDENCY_MISSING',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    RATE_LIMITED: 'RATE_LIMITED',
    TOKEN_BUDGET_EXCEEDED: 'TOKEN_BUDGET_EXCEEDED',
    AI_OUTPUT_INVALID: 'AI_OUTPUT_INVALID',
    AI_PROVIDER_UNAVAILABLE: 'AI_PROVIDER_UNAVAILABLE',
    AI_CAPACITY_EXHAUSTED: 'AI_CAPACITY_EXHAUSTED',
    AI_TIMEOUT: 'AI_TIMEOUT',
    // client-side
    NETWORK_ERROR: 'NETWORK_ERROR',
    UNKNOWN: 'UNKNOWN',
};

// Codes the aiListener already toasts app-wide — pages should not toast these again.
export const GLOBALLY_HANDLED_AI_ERRORS = [
    AI_ERROR_CODES.RATE_LIMITED,
    AI_ERROR_CODES.TOKEN_BUDGET_EXCEEDED,
    AI_ERROR_CODES.AI_CAPACITY_EXHAUSTED,
];

const STATUS_FALLBACK_CODES = {
    400: AI_ERROR_CODES.VALIDATION_FAILED,
    403: AI_ERROR_CODES.ORIGIN_NOT_ALLOWED,
    409: AI_ERROR_CODES.STEP_DEPENDENCY_MISSING,
    413: AI_ERROR_CODES.PAYLOAD_TOO_LARGE,
    429: AI_ERROR_CODES.RATE_LIMITED,
    502: AI_ERROR_CODES.AI_OUTPUT_INVALID,
    503: AI_ERROR_CODES.AI_PROVIDER_UNAVAILABLE,
    504: AI_ERROR_CODES.AI_TIMEOUT,
};

const parseRetryAfter = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
    const date = Date.parse(value);
    return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

/**
 * Turn a fetchBaseQuery error into `{ status, code, message, retryAfter, details }`.
 * @param {Object} error - fetchBaseQuery error (`{ status, data }` or `{ status: 'FETCH_ERROR' | ... }`)
 * @param {Object} [meta] - fetchBaseQuery meta (`{ request, response }`)
 */
export const normalizeAiError = (error, meta) => {
    if (typeof error?.status === 'number') {
        const body = error.data?.error || {};
        return {
            status: error.status,
            code: body.code || STATUS_FALLBACK_CODES[error.status] || AI_ERROR_CODES.UNKNOWN,
            message: body.message || null,
            retryAfter: parseRetryAfter(body.retryAfter ?? meta?.response?.headers?.get('Retry-After')),
            details: body.details || null,
        };
    }

    switch (error?.status) {
        case 'TIMEOUT_ERROR':
            return { status: error.status, code: AI_ERROR_CODES.AI_TIMEOUT, message: null, retryAfter: null, details: null };
        case 'FETCH_ERROR':
            return { status: error.status, code: AI_ERROR_CODES.NETWORK_ERROR, message: null, retryAfter: null, details: null };
        default:
            return { status: error?.status ?? null, code: AI_ERROR_CODES.UNKNOWN, message: error?.error || null, retryAfter: null, details: null };
    }
};

/** User-facing text for a normalized AI error. */
export const aiErrorMessage = (error) => {
    switch (error?.code) {
        case AI_ERROR_CODES.RATE_LIMITED:
            return error.retryAfter
                ? `Too many requests. Please try again in ${error.retryAfter}s.`
                : 'Too many requests. Please try again in a moment.';
        case AI_ERROR_CODES.TOKEN_BUDGET_EXCEEDED:
            return 'You have reached the daily AI limit. Please try again tomorrow.';
        case AI_ERROR_CODES.AI_CAPACITY_EXHAUSTED:
        case AI_ERROR_CODES.AI_PROVIDER_UNAVAILABLE:
            return 'AI is temporarily unavailable. Please try again later.';
        case AI_ERROR_CODES.STEP_DEPENDENCY_MISSING:
            return 'Please complete the previous steps first.';
        case AI_ERROR_CODES.VALIDATION_FAILED:
            return 'Some of the information sent to the AI is missing or too long.';
        case AI_ERROR_CODES.PAYLOAD_TOO_LARGE:
            return 'The file or request is too large.';
        case AI_ERROR_CODES.AI_TIMEOUT:
            return 'The AI took too long to respond. Please try again.';
        case AI_ERROR_CODES.NETWORK_ERROR:
            return 'Could not reach the AI service. Check your connection and try again.';
        default:
            return 'AI generation failed. Please try again.';
    }
};

/** True when the aiListener already showed a toast for this error. */
export const isGloballyHandledAiError = (error) => GLOBALLY_HANDLED_AI_ERRORS.includes(error?.code);

// ─── AI service base query ───────────────────────────────────────────────────

if (!AI_API_BASE_URL && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('aiApi: REACT_APP_AI_API_URL is not set — AI requests will fail.');
}

// No auth in v1: the AI service does not verify users yet, so the JWT is not sent to it.
const rawBaseQuery = createBaseQuery({
    baseUrl: AI_API_BASE_URL,
    auth: false,
    credentials: 'omit',
    timeout: 120_000, // longform generations run 60–90s on the service
});

/**
 * Base query for aiApi.
 * - `withProvider: true` on a request adds the user's model choice (`state.ai.provider`) to the JSON body.
 * - Errors are normalized to `{ status, code, message, retryAfter, details }`.
 */
export const aiBaseQuery = async (args, api, extraOptions) => {
    let request = args;

    if (typeof args === 'object' && args !== null) {
        const { withProvider, ...rest } = args;
        request = rest;

        const isJsonBody = rest.body && typeof rest.body === 'object' && !(rest.body instanceof FormData);
        if (withProvider && isJsonBody) {
            request = { ...rest, body: { provider: api.getState().ai.provider, ...rest.body } };
        }
    }

    const result = await rawBaseQuery(request, api, extraOptions);
    if (result.error) {
        return { ...result, error: normalizeAiError(result.error, result.meta) };
    }
    return result;
};
